/**
 * MapExplorerPanel
 *
 * Browsable gallery of saved maps. Supports compact mode for inline GMTray
 * and full mode for GM Assistant popup.
 */

import { useState } from 'react';
import { useMapStore } from '../../stores/mapStore';
import { Icon } from '../Icon';
import type { SavedMapSummary } from '@machina/core';

const GOLD = '#d4af37';
const RED = '#ef4444';
const SILVER = '#c0c0c0';

const ENV_LABELS: Record<string, string> = {
  dungeon_stone: 'Dungeon',
  dungeon_crypt: 'Crypt',
  dungeon_sewer: 'Sewer',
  cave_natural: 'Cave',
  cave_lava: 'Lava Cave',
  forest_clearing: 'Forest',
  forest_dense: 'Dense Forest',
  jungle_river: 'Jungle River',
  jungle_ruins: 'Jungle Ruins',
  jungle_clearing: 'Jungle',
  tavern_interior: 'Tavern',
  castle_interior: 'Castle',
  castle_courtyard: 'Courtyard',
  street_medieval: 'Street',
  marketplace: 'Market',
  docks: 'Docks',
  ship_deck: 'Ship',
  arena: 'Arena',
  racetrack: 'Racetrack',
  graveyard: 'Graveyard',
  bridge: 'Bridge',
  swamp: 'Swamp',
  desert: 'Desert',
  mountain: 'Mountain',
  beach: 'Beach',
  snow: 'Snow',
  arcane_library: 'Library',
  throne_room: 'Throne',
};

export function MapExplorerPanel({ compact }: { compact?: boolean }) {
  const savedMaps = useMapStore((s) => s.savedMaps);
  const loadSavedMapById = useMapStore((s) => s.loadSavedMapById);
  const deleteSavedMap = useMapStore((s) => s.deleteSavedMap);
  const libraryError = useMapStore((s) => s.libraryError);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const isBusy = loadingId !== null || deletingId !== null;

  if (savedMaps.length === 0) {
    return (
      <div style={{
        padding: compact ? 8 : 20,
        textAlign: 'center',
        color: 'rgba(255,255,255,0.3)',
        fontSize: 12,
        fontStyle: 'italic',
      }}>
        No saved maps yet. Generate a map and save it to your library.
      </div>
    );
  }

  const thumbSize = compact ? 60 : 80;

  const handleLoad = async (mapId: string) => {
    if (isBusy) return;
    setLoadingId(mapId);
    try {
      await loadSavedMapById(mapId);
    } catch (err) {
      console.error('[MapExplorerPanel] Load failed:', err);
    } finally {
      setLoadingId(null);
    }
  };

  const handleDelete = async (mapId: string) => {
    if (isBusy) return;
    setDeletingId(mapId);
    try {
      await deleteSavedMap(mapId);
    } catch (err) {
      console.error('[MapExplorerPanel] Delete failed:', err);
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 4 : 6 }}>
      {libraryError && (
        <div style={{
          padding: '4px 8px',
          fontSize: 11,
          color: '#ef4444',
          background: 'rgba(239, 68, 68, 0.08)',
          borderRadius: 3,
        }}>
          {libraryError}
        </div>
      )}
      {savedMaps.map((map) => (
        <MapCard
          key={map.id}
          map={map}
          compact={compact}
          thumbSize={thumbSize}
          isLoading={loadingId === map.id}
          isDeleting={deletingId === map.id}
          isBusy={isBusy}
          isConfirmingDelete={confirmDeleteId === map.id}
          onLoad={() => handleLoad(map.id)}
          onDeleteClick={() => setConfirmDeleteId(map.id)}
          onDeleteConfirm={() => handleDelete(map.id)}
          onDeleteCancel={() => setConfirmDeleteId(null)}
        />
      ))}
    </div>
  );
}

function MapCard({
  map,
  compact,
  thumbSize,
  isLoading,
  isDeleting,
  isBusy,
  isConfirmingDelete,
  onLoad,
  onDeleteClick,
  onDeleteConfirm,
  onDeleteCancel,
}: {
  map: SavedMapSummary;
  compact?: boolean;
  thumbSize: number;
  isLoading: boolean;
  isDeleting: boolean;
  isBusy: boolean;
  isConfirmingDelete: boolean;
  onLoad: () => void;
  onDeleteClick: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}) {
  const envLabel = map.environment ? (ENV_LABELS[map.environment] ?? map.environment) : null;
  const hasThumb = map.thumbnailUrl && !map.thumbnailUrl.startsWith('data:');
  const disabled = isLoading || isDeleting || isBusy;

  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        padding: compact ? 6 : 8,
        background: 'rgba(20, 15, 30, 0.6)',
        border: `1px solid rgba(255,255,255,0.06)`,
        borderRadius: 4,
        cursor: disabled ? 'wait' : 'pointer',
        opacity: (isLoading || isDeleting) ? 0.6 : 1,
        transition: 'opacity 0.2s',
      }}
      onClick={disabled ? undefined : onLoad}
    >
      {/* Thumbnail */}
      <div style={{
        width: thumbSize,
        height: thumbSize,
        borderRadius: 3,
        overflow: 'hidden',
        flexShrink: 0,
        background: 'rgba(255,255,255,0.04)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {hasThumb ? (
          <img
            src={map.thumbnailUrl!}
            alt={map.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Icon name="map" size={compact ? 18 : 24} color="rgba(255,255,255,0.15)" />
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
        <div style={{
          fontSize: compact ? 12 : 13,
          fontWeight: 600,
          color: 'rgba(255,255,255,0.85)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {map.name}
        </div>

        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {envLabel && (
            <span style={{
              fontSize: 9,
              fontWeight: 700,
              color: GOLD,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              padding: '1px 4px',
              borderRadius: 2,
              background: 'rgba(212, 175, 55, 0.1)',
            }}>
              {envLabel}
            </span>
          )}
          {map.mood && (
            <span style={{
              fontSize: 9,
              color: SILVER,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              padding: '1px 4px',
              borderRadius: 2,
              background: 'rgba(192, 192, 192, 0.08)',
            }}>
              {map.mood.replace(/_/g, ' ')}
            </span>
          )}
        </div>

        {map.description && !compact && (
          <div style={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.4)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {map.description}
          </div>
        )}
      </div>

      {/* Actions */}
      <div
        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}
        onClick={(e) => e.stopPropagation()}
      >
        {isConfirmingDelete ? (
          <div style={{ display: 'flex', gap: 2 }}>
            <button
              onClick={onDeleteConfirm}
              disabled={isDeleting}
              style={{
                padding: '2px 6px',
                background: 'rgba(239, 68, 68, 0.15)',
                border: `1px solid rgba(239, 68, 68, 0.4)`,
                borderRadius: 2,
                color: RED,
                cursor: isDeleting ? 'wait' : 'pointer',
                fontSize: 9,
                fontWeight: 700,
                opacity: isDeleting ? 0.6 : 1,
              }}
            >
              {isDeleting ? '...' : 'Delete'}
            </button>
            <button
              onClick={onDeleteCancel}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.3)',
                cursor: 'pointer',
                padding: 2,
              }}
            >
              <Icon name="close" size={10} />
            </button>
          </div>
        ) : (
          <button
            onClick={onDeleteClick}
            title="Delete map"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.2)',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            <Icon name="trash" size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
